import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

// Preferência: 'system' (padrão, igual ao computador), 'light' ou 'dark'.
// Chave nova de propósito: a antiga ('theme') gravava 'light' para todo mundo
// na primeira visita, mesmo sem ninguém escolher, e prenderia todos no claro.
const STORAGE_KEY = 'theme-pref';
const MEDIA = '(prefers-color-scheme: dark)';

const readPref = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    return 'system';
  }
};

const systemIsDark = () => typeof window !== 'undefined' && window.matchMedia?.(MEDIA).matches;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(readPref);
  const [systemDark, setSystemDark] = useState(systemIsDark);

  // Acompanha o computador enquanto a preferência for 'system'.
  useEffect(() => {
    const mq = window.matchMedia?.(MEDIA);
    if (!mq) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  const setPreference = useCallback((pref) => {
    setPreferenceState(pref);
    try {
      if (pref === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // sem storage (janela privada): vale só para esta sessão
    }
  }, []);

  const value = useMemo(
    () => ({ theme, preference, setPreference }),
    [theme, preference, setPreference]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
